import type React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { RemiSpinner } from "@remi/next-ui";

import { cn } from "~/lib/utils";

const buttonBaseClasses =
  "inline-flex items-center justify-center truncate rounded-md font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-border-accent whitespace-nowrap focus-visible:ring-offset-2 ring-offset-background disabled:pointer-events-none w-fit h-fit gap-1";

type ButtonVariant =
  | "default"
  | "primary"
  | "destructive"
  | "outline"
  | "secondary"
  | "secondaryDestructive"
  | "muted"
  | "none"
  | "comboBox"
  | "comboBoxMuted"
  | "dropdown"
  | "pill";
type ButtonVariantClasses = Record<ButtonVariant, { button: string; icon: string }>;

const buttonVariantClasses: ButtonVariantClasses = {
  default: {
    button:
      "bg-button-bgPrimary text-button-textPrimary hover:bg-button-bgPrimaryHover disabled:bg-button-bgPrimaryDisabled disabled:text-button-textPrimaryDisabled",
    icon: "text-button-iconPrimary disabled:text-button-iconPrimaryDisabled",
  },
  primary: {
    button:
      "bg-button-bgPrimary text-button-textPrimary hover:bg-button-bgPrimaryHover disabled:bg-button-bgPrimaryDisabled disabled:text-button-textPrimaryDisabled",
    icon: "text-icon-faint disabled:text-button-iconPrimaryDisabled",
  },
  secondary: {
    button:
      "bg-button-bgSecondary shadow-secondary-button disabled:shadow-none text-text-secondary hover:bg-button-bgSecondaryHover disabled:text-button-textSecondaryDisabled disabled:bg-button-bgSecondaryDisabled",
    icon: "text-icon-secondary disabled:text-icon-tertiary",
  },
  secondaryDestructive: {
    button:
      "bg-button-bgSecondary shadow-secondary-button disabled:shadow-none text-text-destructive hover:bg-button-bgSecondaryHover  disabled:bg-button-bgSecondaryDisabled",
    icon: "text-icon-secondary disabled:text-icon-tertiary",
  },
  destructive: {
    button:
      "bg-button-bgDestructive text-button-textDestructive hover:bg-button-bgDestructiveHover disabled:bg-button-bgDestructiveDisabled",
    icon: "text-button-iconDestructive",
  },
  muted: {
    button:
      " text-text-secondary hover:bg-button-bgTertiaryHover hover:text-text-secondary disabled:bg-button-bgMutedDisabled disabled:text-text-tertiary",
    icon: "text-icon-secondary disabled:text-icon-tertiary",
  },
  outline: {
    button: "border border-input hover:bg-button-bgSecondaryHover",
    icon: "text-icon-secondary",
  },
  comboBox: {
    button: "text-text-secondary bg-background-secondary",
    icon: "",
  },
  comboBoxMuted: {
    button: "text-text-secondary hover:bg-background-secondaryHover",
    icon: "",
  },
  none: {
    button: "",
    icon: "",
  },
  dropdown: {
    button: "w-full h-full justify-start gap-x-2",
    icon: "",
  },
  pill: {
    button:
      "border-border-primary border text-text-primary bg-background-secondary hover:bg-background-secondaryHover disabled:text-text-tertiary",
    icon: "disabled:text-icon-tertiary", // TODO: The way icons are handled, icons don't take the className from here. A wrapper div does. So this is not used.
  },
};

const hoverVariantClasses: ButtonVariantClasses = {
  default: {
    button: "bg-button-bgPrimaryHover text-button-textPrimary",
    icon: "text-button-iconPrimary",
  },
  primary: {
    button: "bg-button-bgPrimaryHover text-button-textPrimary",
    icon: "text-button-iconPrimary ",
  },
  secondary: {
    button: "bg-button-bgSecondaryHover text-text-secondary shadow-secondary-button",
    icon: "text-icon-secondary",
  },
  destructive: {
    button: "bg-button-bgDestructiveHover text-button-textDestructive",
    icon: "text-button-iconDestructive ",
  },
  secondaryDestructive: {
    button: "bg-button-bgSecondaryHover text-text-destructive shadow-secondary-button",
    icon: "text-icon-secondary",
  },
  muted: {
    button: "bg-button-bgTertiaryHover text-text-secondary",
    icon: "text-icon-secondary",
  },
  comboBox: {
    button: "bg-background-secondaryHover text-text-secondary",
    icon: "",
  },
  comboBoxMuted: {
    button: "text-text-secondary hover:bg-background-secondaryHover",
    icon: "",
  },
  outline: {
    button: "bg-button-bgSecondaryHover",
    icon: "",
  },
  none: {
    button: "",
    icon: "",
  },
  dropdown: {
    button: "",
    icon: "",
  },
  pill: {
    button: "bg-background-secondaryHover",
    icon: "",
  },
};

const sizeVariantClasses = {
  icon: "w-7 h-7 px-0",
  default: "px-2 py-1.5 text-xs h-7",
  none: "h-fit",
  small: "px-2 py-1 text-xs",
  chip: "px-2 py-1 text-xs h-5 rounded-full",
  large: "px-3 py-[10px] text-sm",
  largeIcon: "w-10 h-10 px-0",
  pill: "px-2 py-1 text-xs h-6 rounded-full font-normal",
};

const buttonVariants = cva(buttonBaseClasses, {
  variants: {
    variant: buttonVariantClasses,
    size: sizeVariantClasses,
    mockHover: hoverVariantClasses,
  },
  defaultVariants: {
    variant: "default",
    size: "default",
    mockHover: "none",
  },
});

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
}

const getVariantClass = (variantClasses: ButtonVariantClasses, variant: ButtonVariant, type: "button" | "icon") =>
  variantClasses[variant][type];

/** @deprecated use RemiButton */
const Button = ({
  ref,
  className,
  variant = "default",
  size,
  mockHover,
  asChild = false,
  icon,
  children,
  loading,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const buttonClass = getVariantClass(buttonVariantClasses, variant || "default", "button");
  const hoverClass = getVariantClass(hoverVariantClasses, mockHover || "none", "button");
  const iconClass = getVariantClass(buttonVariantClasses, variant || "default", "icon");
  const hoverIconClass = getVariantClass(hoverVariantClasses, mockHover || "none", "icon");

  const ButtonComponent = asChild ? Slot : "button";
  const buttonClasses = cn(buttonClass, hoverClass, buttonVariants({ variant, size, className, mockHover }));
  const iconClasses = cn(iconClass, hoverIconClass);

  const renderIcon = () => {
    if (loading) {
      return <RemiSpinner />;
    }

    if (icon) {
      return <div className={iconClasses}>{icon}</div>;
    }

    return null;
  };

  return (
    <ButtonComponent className={buttonClasses} ref={ref} {...props}>
      {renderIcon()}
      {children}
    </ButtonComponent>
  );
};
Button.displayName = "Button";

export { Button };
